/**
 * Bulk "Move to Prod / Move to Dev" for wallet shop products
 * (`POST /wallet/t/bulkUpdateWalletShopProductEnvironment`, see docs/wallet-shop-endpoints.md).
 *
 * Pure helpers only — request building, batching, result merging and the summary text — so the
 * list page stays thin and these rules are unit tested.
 */

export type Environment = 'dev' | 'prod';

/** Server limit per request; larger selections are sent in batches of this size. */
export const MAX_ENVIRONMENT_MOVE_KEYS = 500;

export type BulkEnvironmentMoveRequest = {
  ProductKeys: string[];
  FromEnvironment: Environment;
  ToEnvironment: Environment;
  Force?: boolean;
};

export type BlockedMove = { ProductKey: string; Reason: string; Code?: string };

export type BulkEnvironmentMoveResult = {
  Requested: number;
  Moved: string[];
  Blocked: BlockedMove[];
  NotFound: string[];
};

export const otherEnvironment = (env: Environment): Environment => (env === 'prod' ? 'dev' : 'prod');
export const environmentLabel = (env: Environment) => (env === 'prod' ? 'Prod' : 'Dev');

/**
 * The single environment a selection can be moved out of, or why it can't. A selection that spans
 * dev and prod (Env: All) is refused rather than split: one click would otherwise move products
 * in opposite directions, and the dangerous prod → dev half would be easy to miss.
 */
export function selectionSourceEnvironment(
  items: { environment?: Environment }[]
): { from: Environment; to: Environment } | { error: string } {
  if (!items.length) return { error: 'Select products to move.' };
  const envs = new Set(items.map((item) => item.environment || 'prod'));
  if (envs.size > 1) {
    return {
      error: 'Selection spans dev and prod. Set Env to Dev or Prod (or select products from one environment) to move them.',
    };
  }
  const from = Array.from(envs)[0] as Environment;
  return { from, to: otherEnvironment(from) };
}

/**
 * Request bodies for a move: trimmed, de-duplicated keys in batches of at most
 * MAX_ENVIRONMENT_MOVE_KEYS. `Force` is only sent when true, and only for prod → dev (it overrides
 * the sold-in-prod block; it never applies to dev → prod).
 */
export function buildEnvironmentMoveRequests(
  productKeys: string[],
  from: Environment,
  to: Environment,
  force = false
): BulkEnvironmentMoveRequest[] {
  if (from === to) throw new Error('FromEnvironment and ToEnvironment must differ.');
  const keys = Array.from(new Set(productKeys.map((key) => (key || '').trim()).filter(Boolean)));
  if (!keys.length) throw new Error('Select at least one product to move.');
  const requests: BulkEnvironmentMoveRequest[] = [];
  for (let i = 0; i < keys.length; i += MAX_ENVIRONMENT_MOVE_KEYS) {
    requests.push({
      ProductKeys: keys.slice(i, i + MAX_ENVIRONMENT_MOVE_KEYS),
      FromEnvironment: from,
      ToEnvironment: to,
      ...(force && from === 'prod' && to === 'dev' ? { Force: true } : {}),
    });
  }
  return requests;
}

/** Normalises one response's `data` (missing arrays become empty). */
export function parseEnvironmentMoveResult(data: any, fallbackRequested = 0): BulkEnvironmentMoveResult {
  const strings = (value: unknown) => (Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []);
  const blocked = Array.isArray(data?.Blocked)
    ? data.Blocked.filter((b: any) => b && typeof b.ProductKey === 'string').map((b: any) => ({
        ProductKey: b.ProductKey,
        Reason: typeof b.Reason === 'string' && b.Reason.trim() ? b.Reason : 'Blocked by the server',
        ...(typeof b.Code === 'string' ? { Code: b.Code } : {}),
      }))
    : [];
  return {
    Requested: typeof data?.Requested === 'number' ? data.Requested : fallbackRequested,
    Moved: strings(data?.Moved),
    Blocked: blocked,
    NotFound: strings(data?.NotFound),
  };
}

export const emptyMoveResult = (): BulkEnvironmentMoveResult => ({ Requested: 0, Moved: [], Blocked: [], NotFound: [] });

/** Combines batch results (or a first attempt with its Force retry). */
export function mergeEnvironmentMoveResults(...results: BulkEnvironmentMoveResult[]): BulkEnvironmentMoveResult {
  return results.reduce(
    (acc, result) => ({
      Requested: acc.Requested + result.Requested,
      Moved: [...acc.Moved, ...result.Moved],
      Blocked: [...acc.Blocked, ...result.Blocked],
      NotFound: [...acc.NotFound, ...result.NotFound],
    }),
    emptyMoveResult()
  );
}

/**
 * After a Force retry of `retriedKeys`: keep everything from the first attempt, drop the retried
 * keys from its Blocked list, and add what the retry moved / still blocked / didn't find.
 */
export function applyForceRetry(
  previous: BulkEnvironmentMoveResult,
  retriedKeys: string[],
  retry: BulkEnvironmentMoveResult
): BulkEnvironmentMoveResult {
  const retried = new Set(retriedKeys);
  return {
    Requested: previous.Requested,
    Moved: [...previous.Moved, ...retry.Moved],
    Blocked: [...previous.Blocked.filter((b) => !retried.has(b.ProductKey)), ...retry.Blocked],
    NotFound: [...previous.NotFound, ...retry.NotFound],
  };
}

/**
 * True when `Force` can override this block: the "sold in prod" refusal. Bundle-usage blocks are
 * never forceable. The contract only gives a human-readable Reason, so this keys off its wording
 * ("sold … (send Force to move anyway)" vs "used by active bundle(s): …").
 */
export const isForceableBlock = (reason: string) => {
  const text = (reason || '').toLowerCase();
  if (text.includes('bundle')) return false;
  return /\bforce\b/.test(text) || /\bsold\b/.test(text);
};

/** Blocked items a "Move anyway" retry may include — only for prod → dev. */
export function forceableBlockedKeys(blocked: BlockedMove[], from: Environment, to: Environment): string[] {
  if (!(from === 'prod' && to === 'dev')) return [];
  // Prefer the server's machine-readable Code; fall back to the Reason wording.
  return blocked
    .filter((b) => (b.Code ? b.Code === 'SOLD_IN_PROD' : isForceableBlock(b.Reason)))
    .map((b) => b.ProductKey);
}

export type MoveSummary = { tone: 'success' | 'warn'; text: string };

/** Toast text: "Moved 3 to prod · 1 blocked · 2 not found". */
export function summarizeEnvironmentMove(result: BulkEnvironmentMoveResult, to: Environment): MoveSummary {
  const parts = [`Moved ${result.Moved.length} to ${to}`];
  if (result.Blocked.length) parts.push(`${result.Blocked.length} blocked`);
  if (result.NotFound.length) parts.push(`${result.NotFound.length} not found`);
  return {
    tone: result.Blocked.length || result.NotFound.length || !result.Moved.length ? 'warn' : 'success',
    text: parts.join(' · '),
  };
}
